import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import FeatureGate from '../FeatureGate/FeatureGate'
import ProfileCard from './Components/ProfileCard'
import ExternalProfile from './Components/ExternalProfile'
import CareerSummary from './Components/CareerSummary'
import RoleDetailsSection from './Components/RoleDetailsSection'
import CareerHistorySection from './Components/CareerHistorySection'
import EducationSection from './Components/EducationalSection'
import CredentialingSection from './Components/CredentialingSection'
import ReminderNotesSection from './Components/ReminderNotesSection'
import CertificationSection from './Components/CertificationSection'
import Loader from '../../components/Loading/Loader'
import CreditsBanner from '../../components/CreditsBanner/CreditsBanner'
import BuyCreditsModal from '../../components/BuyCreditsModal/BuyCreditsModal'
import { getExternalUserDetailInit, getOneUserSkillListing } from '../../redux/actions'
import {
  getSelectedPlanFromPlanCode,
  canUseSubscriptionFeatures,
  openManageSubscriptionModal,
  openUpgradePlanModal,
} from '../../shared/billing'
import {
  CREDIT_ALERT_TYPE_PAYMENT_FAILED,
  CREDIT_ALERT_TYPE_PILOT_EXPIRED,
  CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
  CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON,
  CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
} from '../../shared/creditsBanner'
import styles from './InternalExternalDetail.module.css'

const shouldOpenUpgradePlanModalFromBanner = (payload = {}) =>
  [
    CREDIT_ALERT_TYPE_PAYMENT_FAILED,
    CREDIT_ALERT_TYPE_PILOT_EXPIRED,
    CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
  ].includes(String(payload?.type || '').toUpperCase())

const shouldOpenManageSubscriptionModalFromBanner = (payload = {}) =>
  [
    CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRING_SOON,
    CREDIT_ALERT_TYPE_TRIAL_ENDING_SOON,
  ].includes(String(payload?.type || '').toUpperCase())

const shouldOpenSubscriptionModalFromBannerButton = (payload = {}) =>
  String(
    payload?.buttonText || payload?.ctaText || payload?.actionText || ''
  ).toLowerCase() === 'upgrade plan'

const shouldOpenBuyCreditsModalFromBanner = (payload = {}) =>
  ['buy credits', 'buy more'].includes(
    String(
      payload?.buttonText || payload?.ctaText || payload?.actionText || ''
    ).toLowerCase()
  )

const InternalExternalDetail = ({ mode = 'internal' }) => {
  const { id } = useParams()
  const isInternal = mode === 'internal'
  const userProfile = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile'))
    } catch (error) {
      return null
    }
  }, [])
  const isMasterAccount =
    userProfile?.MasterAccountId === null ||
    userProfile?.MasterAccountId === undefined
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures(userProfile)
  const professionCode = userProfile?.Professions?.ProfessionCode || ''
  const [showBuyCreditsModal, setShowBuyCreditsModal] = React.useState(false)
  const [creditsBanner, setCreditsBanner] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem('creditsBanner'))
    } catch (error) {
      return null
    }
  })

  const dispatch = useDispatch()
  const authToken = useSelector((state) => state.authReducer?.user?.authToken)
  const externalLoading = useSelector((state) => state.externalUserDetailReducer.loading)
  const internalLoading = useSelector((state) => state.oneUserskillListingReducer.loading)
  const creditsBalance = useSelector((state) => state.plansReducer?.creditsBalance)
  const creditsBalanceLoaded = useSelector((state) => state.plansReducer?.creditsBalanceLoaded)

  const refetchProfileDetails = () => {
    if (isInternal && id) {
      dispatch(getOneUserSkillListing({
        skillId: id,
        useGuestEndpoint: !authToken,
      }))
    }
  }

  useEffect(() => {
    if (isInternal && id) {
      dispatch(getOneUserSkillListing({
        skillId: id,
        useGuestEndpoint: !authToken,
      }))
    }
    if (!isInternal && id) {
      dispatch(getExternalUserDetailInit(id))
    }
  }, [id, isInternal, authToken, dispatch])

  useEffect(() => {
    if (!subscriptionFeaturesEnabled) {
      setCreditsBanner(null)
      return undefined
    }

    const syncCreditsBanner = (event) => {
      if (event?.detail !== undefined) {
        setCreditsBanner(event.detail)
        return
      }

      try {
        setCreditsBanner(JSON.parse(localStorage.getItem('creditsBanner')))
      } catch (error) {
        setCreditsBanner(null)
      }
    }

    window.addEventListener('credits-banner-updated', syncCreditsBanner)

    return () => {
      window.removeEventListener('credits-banner-updated', syncCreditsBanner)
    }
  }, [subscriptionFeaturesEnabled])

  if (subscriptionFeaturesEnabled && !creditsBalanceLoaded) return <Loader />
  if (isInternal && internalLoading) return <Loader />
  if (!isInternal && externalLoading) return <Loader />

  if (subscriptionFeaturesEnabled && creditsBalance?.data?.status === 'expired') return (
    <FeatureGate
      featureName="Skill Listing"
      iconType="candidate"
      description="Your plan has expired. You cannot view a talent's skill listing. Choose a plan to continue accessing detailed profiles."
      bottomText="Skill Listing is a paid feature and was given to you for free during your trial period."
    />
  )

  return (
    <div className={styles.page}>
      {subscriptionFeaturesEnabled && <BuyCreditsModal
        visible={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        onUpgradeToPlan={() => {
          setShowBuyCreditsModal(false)
          openUpgradePlanModal('starter')
        }}
        availableCredits={0}
      />}
      {subscriptionFeaturesEnabled && !isInternal && <CreditsBanner
        banner={creditsBanner}
        hideAction={!isMasterAccount}
        onAction={(payload = {}) => {
          const actionUrl =
            payload?.buttonUrl ||
            payload?.ctaUrl ||
            payload?.actionUrl ||
            payload?.redirectUrl ||
            payload?.url

          if (actionUrl) {
            window.location.assign(actionUrl)
            return
          }

          if (shouldOpenBuyCreditsModalFromBanner(payload)) {
            setShowBuyCreditsModal(true)
            return
          }

          if (shouldOpenSubscriptionModalFromBannerButton(payload)) {
            openManageSubscriptionModal({
              selectedPlan: getSelectedPlanFromPlanCode(
                payload?.planCode || payload?.productCode || ''
              ),
              allowCurrentPlanCheckout: true,
            })
            return
          }

          if (shouldOpenManageSubscriptionModalFromBanner(payload)) {
            openManageSubscriptionModal({
              selectedPlan: getSelectedPlanFromPlanCode(
                payload?.planCode || payload?.productCode || ''
              ),
              allowCurrentPlanCheckout: true,
            })
            return
          }

          if (shouldOpenUpgradePlanModalFromBanner(payload)) {
            openUpgradePlanModal(
              getSelectedPlanFromPlanCode(
                payload?.planCode || payload?.productCode || ''
              )
            )
          }
        }}
      />}
      <div
        className={`${styles.wrapper} ${!isInternal && creditsBanner ? styles.wrapperWithBanner : ""
          }`}
      >
        {isInternal ? <ProfileCard id={id} onBidSuccess={refetchProfileDetails} /> : <ExternalProfile id={id} />}


        <main className={styles.content}>
          <CareerSummary mode={mode} />

          {isInternal && <RoleDetailsSection mode={mode} />}

          <CareerHistorySection mode={mode} />

          <EducationSection mode={mode} />

          {isInternal && professionCode === "HC" ? (
            <CredentialingSection mode={mode} />
          ) : (
            <CertificationSection mode={mode} />
          )}

          {isInternal && authToken && <ReminderNotesSection mode={mode} />}
        </main>
      </div>
    </div>
  )
}

export default InternalExternalDetail
